import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { siteConfig } from '../siteConfig';
import { Divider } from '../components/Divider';
import { submitRsvp, type AdditionalGuest, type RsvpPayload } from '../api';
import { loadUser } from '../pexesoStore';
import './Rsvp.css';

/** Split the pexeso name pill into first/last name. If there's a single word,
    treat it as first name and leave last blank for the guest to complete. */
function splitName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length <= 1) return { firstName: parts[0] ?? '', lastName: '' };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

type MealPref = 'omni' | 'veg' | 'vegan';
type Nights = 0 | 1 | 2;
type Status = 'idle' | 'submitting' | 'success' | 'error';

const PARTY_MAX = 10;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Rsvp() {
  const { t, i18n } = useTranslation();
  const deadlinePassed = useMemo(
    () => new Date(siteConfig.event.rsvpDeadlineISO) < new Date(),
    [],
  );

  const prefill = useMemo(() => {
    const u = loadUser();
    return u ? splitName(u.name) : { firstName: '', lastName: '' };
  }, []);

  const [firstName, setFirstName] = useState(prefill.firstName);
  const [lastName, setLastName] = useState(prefill.lastName);
  const [email, setEmail] = useState('');
  const [attending, setAttending] = useState<'yes' | 'no' | null>(null);
  const [partySize, setPartySize] = useState(1);
  const [meal, setMeal] = useState<MealPref>('omni');
  /* One row per additional guest beyond the primary RSVP filler. Synced
     to (partySize - 1) by the effect below; names already typed survive
     when the count grows or shrinks. */
  const [additionalGuests, setAdditionalGuests] = useState<AdditionalGuest[]>([]);
  const [allergies, setAllergies] = useState('');
  const [nights, setNights] = useState<Nights>(1);
  const [childrenCount, setChildrenCount] = useState(0);
  const [childrenAges, setChildrenAges] = useState('');
  const [note, setNote] = useState('');

  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const target = Math.max(0, Math.min(PARTY_MAX - 1, partySize - 1));
    setAdditionalGuests((prev) => {
      if (prev.length === target) return prev;
      if (prev.length < target) {
        const extras: AdditionalGuest[] = Array.from({ length: target - prev.length }, () => ({
          name: '',
          meal: 'omni',
        }));
        return [...prev, ...extras];
      }
      return prev.slice(0, target);
    });
  }, [partySize]);

  function updateGuest(idx: number, patch: Partial<AdditionalGuest>) {
    setAdditionalGuests((prev) => prev.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  }

  function validate(): string | null {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || attending === null) {
      return t('rsvp.errors.required');
    }
    if (!EMAIL_RE.test(email)) {
      return t('rsvp.errors.email');
    }
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validate();
    if (err) {
      setStatus('error');
      setErrorMsg(err);
      return;
    }

    const payload: RsvpPayload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      language: i18n.language.split('-')[0] || 'cs',
      attending: attending === 'yes',
      partySize,
      mealPreference: meal,
      additionalGuests: additionalGuests.map((g) => ({ name: g.name.trim(), meal: g.meal })),
      allergies: allergies.trim(),
      nights,
      childrenCount,
      childrenAges: childrenAges.trim(),
      note: note.trim(),
    };

    setStatus('submitting');
    setErrorMsg('');
    try {
      await submitRsvp(payload);
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMsg(t('rsvp.errors.server'));
    }
  }

  if (status === 'success') {
    return (
      <section id="rsvp" className="section rsvp rsvp--success">
        <Divider />
        <div className="section-header">
          <div className="section-kicker">RSVP</div>
          <h2 className="section-title">{t('rsvp.title')}</h2>
        </div>
        <div className="rsvp__success-box">{t('rsvp.success')}</div>
      </section>
    );
  }

  return (
    <section id="rsvp" className="section rsvp">
      <Divider />
      <div className="section-header">
        <div className="section-kicker">RSVP</div>
        <h2 className="section-title">{t('rsvp.title')}</h2>
        <p className="section-subtitle">{t('rsvp.subtitle')}</p>
      </div>

      {deadlinePassed && (
        <div className="rsvp__deadline-warning">{t('rsvp.deadlinePassed')}</div>
      )}

      <form className="rsvp__form" onSubmit={handleSubmit} noValidate>
        <div className="rsvp__row rsvp__row--2">
          <label>
            <span>{t('rsvp.form.firstName')} *</span>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              autoComplete="given-name"
            />
          </label>
          <label>
            <span>{t('rsvp.form.lastName')} *</span>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              autoComplete="family-name"
            />
          </label>
        </div>

        <label>
          <span>{t('rsvp.form.email')} *</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </label>

        <fieldset className="rsvp__fieldset">
          <legend>{t('rsvp.form.attending')} *</legend>
          <div className="rsvp__choices">
            <label className={`rsvp__choice ${attending === 'yes' ? 'is-active' : ''}`}>
              <input
                type="radio"
                name="attending"
                value="yes"
                checked={attending === 'yes'}
                onChange={() => setAttending('yes')}
              />
              <span>{t('rsvp.form.attendingYes')}</span>
            </label>
            <label className={`rsvp__choice ${attending === 'no' ? 'is-active' : ''}`}>
              <input
                type="radio"
                name="attending"
                value="no"
                checked={attending === 'no'}
                onChange={() => setAttending('no')}
              />
              <span>{t('rsvp.form.attendingNo')}</span>
            </label>
          </div>
        </fieldset>

        {attending === 'yes' && (
          <>
            <div className="rsvp__row rsvp__row--2">
              <label>
                <span>{t('rsvp.form.partySize')}</span>
                {/* Native <select> instead of <input type="number">: iOS
                    Safari has no spinner UI on number inputs, which left
                    mobile guests effectively able to pick only the default
                    (1) or type the max (10). The picker is unambiguous. */}
                <select
                  value={partySize}
                  onChange={(e) => setPartySize(Number(e.target.value))}
                >
                  {Array.from({ length: PARTY_MAX }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>{t('rsvp.form.mealForYou')}</span>
                <select value={meal} onChange={(e) => setMeal(e.target.value as MealPref)}>
                  <option value="omni">{t('rsvp.form.mealOmni')}</option>
                  <option value="veg">{t('rsvp.form.mealVeg')}</option>
                  <option value="vegan">{t('rsvp.form.mealVegan')}</option>
                </select>
              </label>
            </div>

            {additionalGuests.length > 0 && (
              <div className="rsvp__guests">
                {additionalGuests.map((g, i) => (
                  <div key={i} className="rsvp__row rsvp__row--2 rsvp__guest-row">
                    <label>
                      <span>{t('rsvp.form.guestName', { num: i + 2 })}</span>
                      <input
                        type="text"
                        value={g.name}
                        onChange={(e) => updateGuest(i, { name: e.target.value })}
                        placeholder={t('rsvp.form.guestNamePlaceholder')}
                      />
                    </label>
                    <label>
                      <span>{t('rsvp.form.guestMeal')}</span>
                      <select
                        value={g.meal}
                        onChange={(e) => updateGuest(i, { meal: e.target.value as MealPref })}
                      >
                        <option value="omni">{t('rsvp.form.mealOmni')}</option>
                        <option value="veg">{t('rsvp.form.mealVeg')}</option>
                        <option value="vegan">{t('rsvp.form.mealVegan')}</option>
                      </select>
                    </label>
                  </div>
                ))}
              </div>
            )}

            <label>
              <span>{t('rsvp.form.allergies')}</span>
              <input
                type="text"
                value={allergies}
                onChange={(e) => setAllergies(e.target.value)}
                placeholder={t('rsvp.form.allergiesPlaceholder')}
              />
            </label>

            <label>
              <span>{t('rsvp.form.nights')}</span>
              <select value={nights} onChange={(e) => setNights(Number(e.target.value) as Nights)}>
                <option value={0}>{t('rsvp.form.nightsNone')}</option>
                <option value={1}>{t('rsvp.form.nightsOne')}</option>
                <option value={2}>{t('rsvp.form.nightsTwo')}</option>
              </select>
            </label>

            <div className="rsvp__row rsvp__row--2">
              <label>
                <span>{t('rsvp.form.children')}</span>
                <select
                  value={childrenCount}
                  onChange={(e) => setChildrenCount(Number(e.target.value))}
                >
                  {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              {childrenCount > 0 && (
                <label>
                  <span>{t('rsvp.form.childrenAges')}</span>
                  <input
                    type="text"
                    value={childrenAges}
                    onChange={(e) => setChildrenAges(e.target.value)}
                    placeholder="3, 7"
                  />
                </label>
              )}
            </div>
          </>
        )}

        <label>
          <span>{t('rsvp.form.note')}</span>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('rsvp.form.notePlaceholder')}
          />
        </label>

        {status === 'error' && errorMsg && <div className="rsvp__error">{errorMsg}</div>}

        <button type="submit" className="btn rsvp__submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? t('rsvp.form.submitting') : t('rsvp.form.submit')}
        </button>
      </form>
    </section>
  );
}
